import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { savePhones } from './editUtils';
import { saveNotificationPhone } from './profileAPI';
import { getCustomerParty, getPrimaryPhoneInfo, getSecondaryPhoneInfo } from './selectors';
import { useSnackbar } from 'notistack';
import {
  CancelButton,
  PhoneEditContainer,
  PhoneRowContainer,
  PhoneSelect,
  PhoneSelectorContainer,
  PrimaryPhoneLabel,
  PrimaryPhoneNumberInput,
  PrimaryPhoneRequiredField,
  SavePhonesButton,
  SecondaryPhoneNumberInput,
  SecondaryPhoneNumberLabel,
  SecondaryPhoneSelect,
} from './styles';
import { formatPhoneNumber, isPhone } from '../../../utils/validators/generalValidators';
import TooltipInfo from '../../common/tooltip';
import { getText } from '../../../utils/i18n';

const EditingPhones = props => {
  const {
    ariaLabel,
    customerId,
    customerSubscriptionsFull,
    dispatch,
    isDashboard,
    standardPhoneLength,
    notificationPhone,
    phoneNumberPrimaryRetrieved,
    phoneNumberSecondary,
    cancelPhone,
    primaryPhoneNameType,
    secondaryPhoneNameType,
    i18n,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [primaryPhoneNumber, setPrimaryPhoneNumber] = React.useState(
    isDashboard
      ? phoneNumberPrimaryRetrieved
        ? phoneNumberPrimaryRetrieved
        : ''
      : notificationPhone
      ? notificationPhone
      : '',
  );
  const [updatedSecondaryPhoneNumber, setUpdatedSecondaryPhoneNumber] = React.useState(
    phoneNumberSecondary ? phoneNumberSecondary : '',
  );
  const [primaryPhoneTypeSelected, setPrimaryPhoneTypeSelected] = React.useState({
    value: primaryPhoneNameType ? primaryPhoneNameType : null,
    label: primaryPhoneNameType ? primaryPhoneNameType : null,
  });
  const [updatedSecondaryPhoneNameType, setUpdatedSecondaryPhoneNameType] = React.useState({
    value: secondaryPhoneNameType ? secondaryPhoneNameType : null,
    label: secondaryPhoneNameType ? secondaryPhoneNameType : null,
  });
  const arePhoneInputsValid = () => {
    let isValid =
      primaryPhoneNumber !== '' &&
      isPhone(primaryPhoneNumber) &&
      primaryPhoneTypeSelected.value !== null;
    if (isDashboard) {
      if (updatedSecondaryPhoneNumber.trim() !== '') {
        isValid =
          isValid &&
          isPhone(updatedSecondaryPhoneNumber) &&
          updatedSecondaryPhoneNameType.value !== null;
      }
    }
    return isValid;
  };
  const handlePrimaryPhoneChange = phone => {
    setPrimaryPhoneTypeSelected(phone);
  };
  const handleSecondaryPhoneChange = phone => {
    setUpdatedSecondaryPhoneNameType(phone);
  };
  const phoneTypeSelector = [
    { value: null, label: 'Select Type' },
    { value: 'Mobile', label: 'Mobile' },
    { value: 'Home', label: 'Home' },
    { value: 'Business', label: 'Business' },
    { value: 'Other', label: 'Other' },
  ];
  const checkSaveProfileOrNotificationPhone = () => {
    if (isDashboard) {
      dispatch(
        savePhones(
          props,
          primaryPhoneNumber,
          updatedSecondaryPhoneNumber,
          primaryPhoneTypeSelected.value,
          updatedSecondaryPhoneNumber ? updatedSecondaryPhoneNameType.value : null,
          phoneNumberSecondary,
          secondaryPhoneNameType,
          enqueueSnackbar,
        ),
      );
    } else {
      dispatch(
        saveNotificationPhone(
          customerId,
          customerSubscriptionsFull,
          primaryPhoneNumber,
          enqueueSnackbar,
        ),
      );
    }
  };
  return (
    <>
      {isDashboard ? (
        <PrimaryPhoneLabel>
          Primary phone <TooltipInfo title={getText(i18n, 'profile.primaryPhoneTooltip')} />
        </PrimaryPhoneLabel>
      ) : null}
      <PhoneEditContainer isDashboard={isDashboard}>
        <PhoneRowContainer isDashboard={isDashboard}>
          <PrimaryPhoneNumberInput isDashboard={isDashboard}>
            <input
              aria-label={ariaLabel}
              value={primaryPhoneNumber}
              onChange={e => setPrimaryPhoneNumber(formatPhoneNumber(e.target.value))}
              maxLength={standardPhoneLength ? 14 : 20}
            />
          </PrimaryPhoneNumberInput>
          {isDashboard ? (
            <PhoneSelectorContainer>
              <PhoneSelect
                id="phoneTypeSelector"
                isDashboard={isDashboard}
                options={phoneTypeSelector}
                value={primaryPhoneTypeSelected}
                onChange={handlePrimaryPhoneChange}
              />
              <PrimaryPhoneRequiredField>*</PrimaryPhoneRequiredField>
            </PhoneSelectorContainer>
          ) : null}
        </PhoneRowContainer>
        {isDashboard ? (
          <SecondaryPhoneNumberLabel>
            Secondary phone <span style={{ color: '#657580' }}>(Optional)</span>
            <TooltipInfo title={getText(i18n, 'profile.secondaryPhoneTooltip')} />
          </SecondaryPhoneNumberLabel>
        ) : null}
        {isDashboard ? (
          <PhoneRowContainer isDashboard={isDashboard}>
            <SecondaryPhoneNumberInput isDashboard={isDashboard}>
              <input
                value={updatedSecondaryPhoneNumber}
                onChange={e => setUpdatedSecondaryPhoneNumber(formatPhoneNumber(e.target.value))}
              />
            </SecondaryPhoneNumberInput>
            <PhoneSelectorContainer>
              <SecondaryPhoneSelect
                isDashboard={isDashboard}
                options={phoneTypeSelector}
                onChange={handleSecondaryPhoneChange}
                value={updatedSecondaryPhoneNameType}
              />
              <PrimaryPhoneRequiredField> </PrimaryPhoneRequiredField>
            </PhoneSelectorContainer>
          </PhoneRowContainer>
        ) : null}
      </PhoneEditContainer>
      <SavePhonesButton
        onClick={() => checkSaveProfileOrNotificationPhone()}
        disabled={!arePhoneInputsValid()}
      >
        Apply
      </SavePhonesButton>
      <CancelButton
        onClick={() => {
          cancelPhone();
        }}
      >
        Cancel
      </CancelButton>
    </>
  );
};

EditingPhones.propTypes = {
  ariaLabel: PropTypes.string,
  cancelPhone: PropTypes.func,
  customerParty: PropTypes.object,
  customerSubscriptionsFull: PropTypes.any,
  customer: PropTypes.object,
  customerId: PropTypes.string,
  dispatch: PropTypes.func,
  isDashboard: PropTypes.bool,
  notificationPhone: PropTypes.string,
  phoneNumberPrimary: PropTypes.string,
  phoneNumberPrimaryId: PropTypes.string,
  phoneNumberPrimaryRetrieved: PropTypes.string,
  phoneNumberSecondary: PropTypes.string,
  phoneNumberSecondaryId: PropTypes.string,
  primaryPhoneName: PropTypes.string,
  primaryPhoneNameType: PropTypes.string,
  secondaryPhoneName: PropTypes.string,
  secondaryPhoneNameType: PropTypes.string,
  standardPhoneLength: PropTypes.bool,
  i18n: PropTypes.object,
};
const mapStateToProps = state => ({
  phoneErrors: state.profileSlice.phoneErrors,
  standardPhoneLength: state.configurationSlice.features.features.standardPhoneLength,
  editingPhone: state.profileSlice.editingPhone,
  customerId: state.loginSlice.user.preferred_username,
  systemId: state.customerSlice.customer.systemId,
  customer: state.customerSlice.customer,
  customerParty: getCustomerParty(state),
  customerSubscriptionsFull: state.notificationSlice.customerSubscriptionsFull,
  notificationPhone: state.notificationSlice.notificationPhone,
  phoneNumberPrimaryRetrieved: getPrimaryPhoneInfo(state).phoneNumber,
  phoneNumberSecondary: getSecondaryPhoneInfo(state).phoneNumber,
  phoneNumberSecondaryRetrieved: getSecondaryPhoneInfo(state).phoneNumber,
  phoneNumberPrimaryId: getPrimaryPhoneInfo(state).id,
  phoneNumberSecondaryId: getSecondaryPhoneInfo(state).id,
  primaryPhoneName: getPrimaryPhoneInfo(state).phoneName,
  secondaryPhoneName: getSecondaryPhoneInfo(state).phoneName,
  primaryPhoneNameType: getPrimaryPhoneInfo(state).phoneName,
  secondaryPhoneNameType: getSecondaryPhoneInfo(state).phoneName,
  i18n: state.i18nSlice.i18n,
});
EditingPhones.defaultProps = {
  state: {},
};

export default connect(mapStateToProps)(EditingPhones);
